import { useContext, useEffect, useRef } from 'react';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ConcernsContext } from '../../../lib/context/ConcernsContext';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { authenticatedFetchWithTenantAnalytics, useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { useAuth } from 'react-oidc-context';
import { GetSurchargesResponse, SurchargeCached } from '~/lib/api/me/types';
import { ApiResponse } from '~/lib/api/api-response';
import { getApiPath } from '~/lib/api/me/get-api-path';
import { capturePromiseException } from '~/lib/metrics';
import useSWR from 'swr';

const useSurchargesBackgroundTasks = () => {
  const { session } = useAnalytics();
  const { tenant, settings } = useTenant();
  const { user } = useAuth();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { dispatch } = useContext(ConcernsContext);

  const { data } = useSWR<SurchargeCached[]>(
    SettingsCache.settings.Features.includes('Surcharges') ? `${tenant}/surcharges/background` : null,
    async () => {
      const res: ApiResponse<GetSurchargesResponse> = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        session,
        getApiPath(tenant, `/surcharge?take=50&skip=0`)
      )
        .then((res) => res.json())
        .catch(capturePromiseException);

      if (res.success && res.result) {
        return res.result.surcharges.map((x) => ({
          id: x.id,
          orderLineId: x.orderLineId,
          orderId: x.orderId
        }));
      }
      return null;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: false,
      refreshWhenHidden: false,
      refreshInterval: (d) => (d === undefined ? 100 : 15 * 60000) // refresh every 15 minutes
    }
  );

  const previousDataRef = useRef<SurchargeCached[] | null | undefined>(data);

  useEffect(() => {
    if (data && data.length > 0 && previousDataRef.current !== data) {
      previousDataRef.current = data;
      dispatch({
        type: 'ADD-CONCERN',
        payload: {
          Title: t('NOTIFICATION_SURCHARGES_TITLE', { defaultValue: 'Courier Surcharges' }),
          Description: t('NOTIFICATION_SURCHARGES_CONTENT', {
            defaultValue: `{{count}} courier surcharge(s) have been generated for orders you have placed.`,
            count: data.length
          }),
          Type: 'surcharge'
        }
      });
    }
  }, [data, dispatch, t]);

  return data;
};

export default useSurchargesBackgroundTasks;
